import { useEffect, useRef } from "react";
import { socketInstance } from "./socket";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import {
  removeChatById,
  updateByChatId,
  updateChatList,
  updateChatListForNewMessage,
  updateUnreadCountByChatId,
} from "../store/Slices/ChatListSlice";
import { ChatListRes, OuterChat } from "../types/ChatListType";
import { MessageList, MessageListRes } from "../types/MessageListType";
import {
  appendMessage,
  appendNewerMessages,
  appendPinnedMessages,
  removePinnedMessagesByIds,
  removeMessagesByIds,
  startMessageSnap,
  updateMessagesByIds,
  // updatePollVote,
} from "../store/Slices/MessageListSlice";
import {
  incrementMissedBelow,
  updateMessageOptions,
} from "../store/Slices/MessageOptionsSlice";
import {
  addOnlineUser,
  initialOnlineUser,
  removeOnlineUser,
} from "../store/Slices/OnlineUserSlice";
import {
  removeArchiveChatById,
  updateArchiveList,
  updateArchiveListForNewMessage,
  updateArchiveListUnreadCountByChatId,
} from "../store/Slices/ArchiveListSlice";
import scrollToMessage from "../utils/scrollToMessage";
import {
  ArchiveChatRes,
  BlockedUpdateRes,
  CallData,
  CallEndedRes,
  ChatMessagesReadRes,
  ConnectedUserListRes,
  DeleteForEveryoneRes,
  PinUnpinRes,
  RealTimeMessageSeenRes,
  ReceivingCallRes,
  UserJoinedRes,
} from "../types/ResType";
import {
  addConnectedUser,
  updateConnectedUserList,
} from "../store/Slices/ConnectedUserSlice";
import { updateViewState } from "../store/Slices/ViewManagerSlice";
import { updateCallData } from "../store/Slices/CallDataSlice";
import { updatePeerData } from "../store/Slices/PeerJsSlice";
import { clearCallSessionKeys, stopAllCallSounds } from "../utils/callSounds";
import {
  beginCallTeardown,
  idlePeerCallPatch,
  leaveCallToChat,
} from "../utils/endCallSession";
import { updateCurrentConversation } from "../store/Slices/CurrentConversationSlice";
import { SingleMessageRes } from "../types/SingleMessageType";
import { useQueryClient } from "react-query";
import { OnlineUser, OnlineUserRes } from "../types/OnlineUserType";
import {
  addTypingUser,
  removeTypingUser,
  TypingUser,
} from "../store/Slices/TypingUserListSlice";
import { store } from "../store/store";
import {
  addUnSeenMessages,
  clearUnSeenMessagesForChat,
} from "../store/Slices/UnSeenMessagesSlice";
import { chatListFullPayload } from "../utils/socketQueries";
import { catchUpChatDifference } from "../utils/fetchChatDifference";
import { isRealMessage } from "../utils/messageListWindow";
import {
  clearPinnedCatalogDisk,
  writePinnedCatalogDisk,
} from "../utils/fetchPinnedMessages";
import { playIncomingMessageSound } from "../utils/notificationSound";
import { setCallPeerSnapshot } from "../utils/callPeerSnapshot";

export default function ListenAllEvents() {
  const dispatch = useAppDispatch();
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const userData = useAppSelector((state) => state.userData);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const MessageList = useAppSelector((state) => state.MessageList);
  // Use useRef to store the latest value of currentConversationData.chat_id
  const currentChatRef = useRef(currentConversationData);
  const messageListRef = useRef(MessageList);
  const userDataRef = useRef(userData);
  const queryClient = useQueryClient();

  useEffect(() => {
    messageListRef.current = MessageList;
  }, [MessageList]);

  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  // Keep ref updated with the latest chat_id
  useEffect(() => {
    currentChatRef.current = currentConversationData;
  }, [currentConversationData]);

  // Tell server which chat is foreground (multi-device: suppress notify on siblings)
  useEffect(() => {
    const emitActive = (chatId: number) => {
      try {
        socketInstance().emit("active_chat", { chat_id: chatId });
      } catch {
        /* socket may not be ready yet */
      }
    };
    const sync = () => {
      const chatId = Number(currentConversationData?.chat_id) || 0;
      // Hidden tab ≠ reading — let other devices notify
      emitActive(
        document.visibilityState === "visible" && chatId > 0 ? chatId : 0,
      );
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      emitActive(0);
    };
  }, [currentConversationData?.chat_id]);

  function appendMessageToMessageList(SingleMessageRes: SingleMessageRes) {
    const message = SingleMessageRes?.Records?.[0];
    if (!message) return;

    const isOpenChat =
      String(currentChatRef.current.chat_id) == String(message.chat_id);
    const myId = Number(userDataRef.current?.user_id);
    const fromOther =
      Number(message.sender_id) !== myId &&
      Number(message.User?.user_id) !== myId;
    // Chat open on another device of mine → no sound / no unread badge here
    const suppressNotify =
      !!(message as any).suppress_notification ||
      (fromOther &&
        message.unseen_count != null &&
        Number(message.unseen_count) === 0 &&
        !isOpenChat);

    if (
      fromOther &&
      message.message_type != "call" &&
      !suppressNotify
    ) {
      playIncomingMessageSound({
        messageId: message.message_id,
        createdAt: message.createdAt,
      });
    }

    if (isOpenChat) {
      // Only skip live glue when the tip was actually trimmed from the window
      // (true deep history). Never block chat-list preview updates.
      const opts = store.getState().MessageOptions;
      const viewingPast = opts.has_more_newer;
      const atBottom = opts.message_list_at_bottom !== false;

      if (viewingPast) {
        dispatch(updateMessageOptions({ has_more_newer: true }));
        // Deep history: don't yank scroll; badge above ↓ arrow
        if (fromOther && message.message_type !== "call") {
          dispatch(incrementMissedBelow());
        }
      } else {
        dispatch(appendMessage(SingleMessageRes));
        if (atBottom) {
          setTimeout(() => {
            scrollToMessage(message.message_id, false, true);
          }, 10);

          intervalRef.current = setInterval(() => {
            if (document.hidden) return;
            socketInstance().emit("messages_seen", {
              chat_id: currentChatRef.current.chat_id,
              up_to_message_id: message.message_id,
              status: "seen",
            });
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }, 1000);
        } else if (fromOther && message.message_type !== "call") {
          // Scrolled up in tip window — keep position, show ↓ badge
          dispatch(incrementMissedBelow());
        }
      }
    } else if (fromOther && !suppressNotify) {
      dispatch(
        addUnSeenMessages({
          chat_id: message.chat_id,
          message_id: message.message_id,
        }),
      );
    } else if (fromOther && suppressNotify) {
      // Sibling device already reading — keep badge at 0
      dispatch(
        updateUnreadCountByChatId({
          chat_id: message.chat_id,
          mode: "set",
          unseen_count: 0,
        }),
      );
      dispatch(clearUnSeenMessagesForChat(message.chat_id));
    }

    try {
      const archivedFor = message.Chat?.archived_for;
      const isArchived =
        Array.isArray(archivedFor) &&
        archivedFor.includes(userDataRef.current.user_id.toString());

      const serverUnseen = Number(message.unseen_count);
      const hasServerUnseen = Number.isFinite(serverUnseen);
      const ownMessage =
        Number(message.sender_id) === myId ||
        Number(message.User?.user_id) === myId;
      const openAtTip =
        isOpenChat && !store.getState().MessageOptions.has_more_newer;
      let listUnseen: number | undefined;
      if (ownMessage || suppressNotify || openAtTip) {
        listUnseen = 0;
      } else if (hasServerUnseen) {
        listUnseen = Math.max(0, serverUnseen);
      }

      if (isArchived) {
        dispatch(
          updateArchiveListForNewMessage({
            chat_id: message.chat_id,
            last_message: message.message_content,
            last_message_type: message.message_type,
            peerUserData: message.peerUserData,
            createdAt: message.createdAt,
            cleared_for: [],
            message_thumbnail: message.message_thumbnail || "",
            message_preview: message.message_preview || "",
            blur_hash: message.blur_hash || message.blurHash || null,
            media_url: message.media_url || "",
            caption: message.caption || "",
            message_id: message.message_id,
            deleted_for_everyone: !!message.deleted_for_everyone,
            unseen_count: listUnseen,
          }),
        );
      } else {
        dispatch(
          updateChatListForNewMessage({
            chat_id: message.chat_id,
            last_message: message.message_content,
            last_message_type: message.message_type,
            peerUserData: message.peerUserData,
            createdAt: message.createdAt,
            Chat: message.Chat,
            cleared_for: [],
            message_thumbnail: message.message_thumbnail || "",
            message_preview: message.message_preview || "",
            blur_hash: message.blur_hash || message.blurHash || null,
            media_url: message.media_url || "",
            caption: message.caption || "",
            message_id: message.message_id,
            deleted_for_everyone: !!message.deleted_for_everyone,
            unseen_count: listUnseen,
          }),
        );
      }

      if (
        listUnseen === undefined &&
        fromOther &&
        !suppressNotify &&
        !isArchived
      ) {
        dispatch(
          updateUnreadCountByChatId({
            chat_id: message.chat_id,
            mode: "increment",
          }),
        );
      }
    } catch (error) {
      console.warn("Failed to update chat list for new message", error);
    }
  }

  function updateMessageListCacheData(newMessage: SingleMessageRes) {
    const message = newMessage.Records[0];

    if (message.message_type === "group-created") return;

    const cacheKey = ["message_list", message.chat_id, 1];

    // ✅ First, get the current cached data
    const oldData = queryClient.getQueryData<any>(cacheKey);

    // ❌ If no cached data, don't touch anything
    if (!oldData || !oldData.message_list?.Records) {
      return;
    }

    // ✅ Proceed to update cache
    const updatedRecords = [message, ...oldData.message_list.Records];

    queryClient.setQueryData(cacheKey, {
      ...oldData,
      message_list: {
        ...oldData.message_list,
        Records: updatedRecords,
      },
    });

  }

  function updateCachedMessageInAllPages({
    chat_id,
    message_id,
    updates,
  }: {
    chat_id: number;
    message_id: number;
    updates: {
      message?: string;
      message_content?: string;
      message_type?: string;
      is_edited?: boolean;
      delete_from_everyone?: boolean;
      pinned?: boolean;
      message_seen_status?: "seen" | "sent";
    };
  }) {
    const cache = queryClient.getQueryCache();

    // Get all queries related to this chat
    const cachedQueries = cache.findAll(["message_list"]).filter((q) => {
      const queryKey = q.queryKey;
      return queryKey[1] === chat_id;
    });

    cachedQueries.forEach((query) => {
      queryClient.setQueryData(query.queryKey, (oldData: any) => {
        if (!oldData || !oldData.message_list?.Records) return oldData;

        const updatedRecords = oldData.message_list.Records.map((msg: any) => {
          if (msg.message_id !== message_id) return msg;

          return {
            ...msg,
            message_content:
              updates.message_content ??
              updates.message ??
              msg.message_content,
            message_type: updates.message_type ?? msg.message_type,
            is_edited: updates.is_edited ?? msg.is_edited,
            deleted_for_everyone:
              updates.delete_from_everyone ?? msg.deleted_for_everyone,
            pinned: updates.pinned ?? msg.pinned,
            message_seen_status:
              updates.message_seen_status ?? msg.message_seen_status,
          };
        });

        return {
          ...oldData,
          message_list: {
            ...oldData.message_list,
            Records: updatedRecords,
          },
        };
      });
    });
  }

  useEffect(() => {
    const socket = socketInstance();

    socket.on("initial_online_user", (onlineUsers: OnlineUserRes) => {
      dispatch(initialOnlineUser(onlineUsers));
    });
    socket.on("online_user", (onlineUser: OnlineUser) => {
      dispatch(addOnlineUser(onlineUser));
    });
    socket.on("offline_user", (offlineUser: OnlineUser) => {
      dispatch(removeOnlineUser(offlineUser.user_id));
    });

    socket.on("typing", (typingUserData: TypingUser) => {
      if (typingUserData.typing) {
        dispatch(addTypingUser(typingUserData));
      } else {
        dispatch(removeTypingUser(typingUserData));
      }
    });

    socket.on("chat_list", (ChatListRes: ChatListRes) => {
      dispatch(updateChatList(ChatListRes));
      dispatch(
        updateViewState({
          is_chatlist_loading: false,
        }),
      );
    });

    socket.on("archived_chat_list", (ArchiveListRes: ChatListRes) => {
      dispatch(updateArchiveList(ArchiveListRes));
    });
    socket.on("archive_chat", (archiveChatRes: ArchiveChatRes) => {
      const { chat_id, isArchived } = archiveChatRes;
      if (isArchived) {
        dispatch(removeChatById(chat_id));
        socket.emit("archived_chat_list", chatListFullPayload());
      } else {
        dispatch(removeArchiveChatById(chat_id));
        socket.emit("chat_list", chatListFullPayload());
      }
    });

    socket.on("lable_updated", () => {
      socket.emit("chat_list", chatListFullPayload());
    });

    // Below logic working but it is appending message at top instead of it's position ==================================================================================
    // socket.on("archive_chat", (archiveChatRes: ArchiveChatRes) => {
    //   const { chat_id, isArchived } = archiveChatRes;

    //   // Find the chat in whichever list it currently exists
    //   let chatToMove: OuterChat | undefined;

    //   if (isArchived) {
    //     const conversation = store
    //       .getState()
    //       .chatList.Chats.find((c) => c.Records[0].chat_id === chat_id);
    //     if (conversation) {
    //       chatToMove = conversation;
    //       dispatch(moveChatToArchive({ chat: chatToMove }));
    //       dispatch(addToArchiveList({ chat: chatToMove })); // add into archive
    //     }
    //   } else {
    //     const conversation = store
    //       .getState()
    //       .archiveList.Chats.find((c) => c.Records[0].chat_id === chat_id);
    //     if (conversation) {
    //       chatToMove = conversation;
    //       dispatch(moveChatFromArchive({ chat: chatToMove }));
    //       dispatch(addToChatList({ chat: chatToMove })); // add into chatlist
    //     }
    //   }
    // });

    socket.on("receive", (SingleMessageRes: SingleMessageRes) => {
      appendMessageToMessageList(SingleMessageRes);
      setTimeout(() => {
        updateMessageListCacheData(SingleMessageRes);
      }, 100);
    });

    socket.on(
      "connected-user-list",
      (ConnectedUserListRes: ConnectedUserListRes) => {
        dispatch(updateConnectedUserList(ConnectedUserListRes.connectedUsers));
      },
    );

    socket.on(
      "update_poll_vote",
      ({
        message_id,
        poll_option_id,
        chat_id,
        user_id,
      }: {
        message_id: number;
        poll_option_id: number;
        chat_id: number;
        user_id: number;
      }) => {

        // dispatch(
        //   updatePollVote({
        //     message_id: message_id,
        //     new_option_id: poll_option_id,
        //     user_id: user_id,
        //   }),
        // );
      },
    );

    // socket.on(
    //   "update_data",
    //   ({
    //     chat_id,
    //     delete_from_everyone_ids,
    //   }: {
    //     chat_id: number;
    //     delete_from_everyone_ids: number[];
    //   }) => {
    //     const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    //     socketInstance().emit("chat_list", {});

    //     //   currentChatRef.current.chat_id,
    //     //   "currentChatRef.current.chat_id",
    //     // );

    //     // Use the current value from the ref
    //     if (chat_id === currentChatRef.current.chat_id) {

    //       if (
    //         delete_from_everyone_ids?.length != 0 &&
    //         delete_from_everyone_ids?.length != undefined
    //       ) {

    //         // update messages in message list ========================================================
    //         delete_from_everyone_ids.map((message_id) => {

    //             typeof message_id,
    //             "type of message_id +++++++++++++++++++++++++++++++",
    //           );

    //           dispatch(
    //             updateMessagesByIds({
    //               message: "🚫 This message was deleted!",
    //               message_id: Number(message_id),
    //               delete_from_everyone: true,
    //             }),
    //           );

    //             messageListRef.current.message_list.Records[
    //               messageListRef.current.message_list.Records.length - 1
    //             ],
    //             "messageListRef.current.message_list.Records.length - 1",
    //           );

    //           // Update chatlist if message is last message in messagelist =============================================
    //           if (
    //             messageListRef.current.message_list.Records[
    //               messageListRef.current.message_list.Records.length - 1
    //             ].message_id == message_id
    //           ) {
    //             dispatch(
    //               updateByChatId({
    //                 chat_id: currentChatRef.current.chat_id,
    //                 last_message: "🚫 This message was deleted!",
    //                 last_message_type: "text",
    //               }),
    //             );
    //           }
    //         });
    //       } else {
    //         socket.emit("message_list", {
    //           chat_id: chat_id,
    //           user_timezone: timeZone,
    //           per_page_message: 1,
    //         });
    //       }
    //     }

    //     // update messages in message list ========================================================
    //     // delete_from_everyone_ids.map((message_id) => {
    //     //   // Update chatlist if message is last message in messagelist =============================================
    //     //   if (
    //     //     messageListRef.current[messageListRef.current.length - 1]
    //     //       .message_id == message_id
    //     //   ) {
    //     //     dispatch(
    //     //       updateByChatId({
    //     //         chat_id: currentChatRef.current.chat_id,
    //     //         last_message: "🚫 This message was deleted!",
    //     //         last_message_type: "text",
    //     //       }),
    //     //     );
    //     //   }
    //     // });
    //   },
    // );

    socket.on(
      "delete_for_everyone",
      (deleteForEveryoneData: DeleteForEveryoneRes) => {
        const { chat_id, message_id } = deleteForEveryoneData;
        const id = Number(message_id);

        if (chat_id === currentChatRef.current.chat_id) {
          dispatch(startMessageSnap([id]));

          const records =
            messageListRef.current.message_list?.Records || [];
          const prev = [...records]
            .reverse()
            .find(
              (m) =>
                m.message_id !== id &&
                m.message_type !== "date" &&
                !m.deleted_for_everyone,
            );
          dispatch(
            updateByChatId({
              chat_id: currentChatRef.current.chat_id,
              last_message: prev?.message_content || "",
              last_message_type: prev?.message_type || "text",
            }),
          );
        } else {
          dispatch(removeMessagesByIds([id]));
        }

        updateCachedMessageInAllPages({
          chat_id: chat_id,
          message_id: message_id,
          updates: {
            delete_from_everyone: true,
          },
        });
      },
    );

    socket.on(
      "real_time_message_seen",
      (realTimeMessageSeenData: RealTimeMessageSeenRes) => {
        dispatch(
          updateMessagesByIds({
            message_id: realTimeMessageSeenData.message_id,
            message_seen_status: "seen",
          }),
        );
        updateCachedMessageInAllPages({
          chat_id: realTimeMessageSeenData.chat_id,
          message_id: realTimeMessageSeenData.message_id,
          updates: {
            message_seen_status: "seen",
          },
        });
      },
    );

    // Batch blue ticks (preferred over N× real_time_message_seen).
    // If payload includes unseen_count for this user — also sync inbox badge.
    socket.on(
      "real_time_messages_seen",
      (batch: {
        chat_id: number;
        message_ids?: number[];
        up_to_message_id?: number;
        status?: string;
        unseen_count?: number;
        user_id?: number;
      }) => {
        if (
          batch?.chat_id != null &&
          batch.unseen_count != null &&
          (batch.user_id == null ||
            String(batch.user_id) === String(userDataRef.current?.user_id))
        ) {
          const nextUnseen = Math.max(0, Number(batch.unseen_count));
          dispatch(
            updateUnreadCountByChatId({
              chat_id: batch.chat_id,
              mode: "set",
              unseen_count: nextUnseen,
            }),
          );
          if (nextUnseen === 0) {
            dispatch(
              updateArchiveListUnreadCountByChatId({
                chat_id: batch.chat_id,
              }),
            );
            dispatch(clearUnSeenMessagesForChat(batch.chat_id));
          }
        }

        const ids =
          Array.isArray(batch.message_ids) && batch.message_ids.length
            ? batch.message_ids
            : batch.up_to_message_id
              ? messageListRef.current.message_list.Records.filter(
                  (m) =>
                    isRealMessage(m) &&
                    Number(m.message_id) <= Number(batch.up_to_message_id) &&
                    Number(m.chat_id) === Number(batch.chat_id),
                ).map((m) => Number(m.message_id))
              : [];
        for (const mid of ids) {
          dispatch(
            updateMessagesByIds({
              message_id: mid,
              message_seen_status: "seen",
            }),
          );
          updateCachedMessageInAllPages({
            chat_id: batch.chat_id,
            message_id: mid,
            updates: { message_seen_status: "seen" },
          });
        }
      },
    );

    // Backfill / async Webpage patch for old or late OG crawl
    socket.on(
      "message_link_preview",
      (patch: {
        message_id: number;
        chat_id: number;
        link_preview?: boolean;
        link_url?: string | null;
        link_title?: string | null;
        link_description?: string | null;
        link_image?: string | null;
        link_favicon?: string | null;
        link_site_name?: string | null;
      }) => {
        if (!patch?.message_id) return;
        dispatch(
          updateMessagesByIds({
            message_id: patch.message_id,
            // Do not flip a text dump into a card-only "link" bubble
            link_preview: patch.link_preview !== false,
            link_url: patch.link_url,
            link_title: patch.link_title,
            link_description: patch.link_description,
            link_image: patch.link_image,
            link_favicon: patch.link_favicon,
            link_site_name: patch.link_site_name,
          }),
        );
      },
    );

    socket.on(
      "message_edited",
      (editedData: {
        message_id: number;
        chat_id: number;
        message_content: string;
        message_type?: string;
        is_edited?: boolean;
      }) => {
        if (!editedData?.message_id) return;
        dispatch(
          updateMessagesByIds({
            message_id: editedData.message_id,
            message: editedData.message_content,
            message_type: editedData.message_type as any,
            is_edited: true,
          }),
        );
        updateCachedMessageInAllPages({
          chat_id: editedData.chat_id,
          message_id: editedData.message_id,
          updates: {
            message_content: editedData.message_content,
            message_type: editedData.message_type,
            is_edited: true,
          } as any,
        });
        const chatPreview = store
          .getState()
          .chatList.Chats.find(
            (c) => c.Records[0]?.chat_id == editedData.chat_id,
          );
        const lastId = chatPreview?.Records[0]?.Messages?.[0]?.message_id;
        if (lastId == editedData.message_id) {
          dispatch(
            updateByChatId({
              chat_id: editedData.chat_id,
              last_message: editedData.message_content,
              last_message_type: editedData.message_type || "text",
            }),
          );
        }
      },
    );

    // Same account read on another session (web ↔ mobile) — sync unread badge
    socket.on("chat_messages_read", (readData: ChatMessagesReadRes) => {
      if (!readData?.chat_id) return;
      if (
        readData.user_id != null &&
        userDataRef.current?.user_id != null &&
        String(readData.user_id) !== String(userDataRef.current.user_id)
      ) {
        return;
      }
      const nextUnseen =
        readData.unseen_count != null
          ? Math.max(0, Number(readData.unseen_count))
          : 0;
      dispatch(
        updateUnreadCountByChatId({
          chat_id: readData.chat_id,
          mode: "set",
          unseen_count: nextUnseen,
        }),
      );
      if (nextUnseen === 0) {
        dispatch(
          updateArchiveListUnreadCountByChatId({
            chat_id: readData.chat_id,
          }),
        );
        dispatch(clearUnSeenMessagesForChat(readData.chat_id));
      }
    });

    socket.on("receiving_call", (callData: ReceivingCallRes) => {
      const incomingId = Number(callData?.call?.call_id);
      const peer = store.getState().PeerJsSlice;
      const view = store.getState().ViewManager;
      const currentId = Number(peer?.call_id || 0);
      if (
        incomingId > 0 &&
        currentId > 0 &&
        incomingId !== currentId &&
        (peer.call_joined || view.show_accept_call_modal)
      ) {
        return;
      }

      // Add user to connected user
      dispatch(
        addConnectedUser({
          profile_image: callData.user.profile_pic,
          user_id: callData.user.user_id,
          full_name: callData.user.full_name,
          user_name: callData.user.user_name,
        }),
      );
      dispatch(updateCallData(callData));
      const conversationData = {
        chat_id: callData.chat.chat_id,
        chat_type: callData.chat.chat_type,
        full_name: callData.user.full_name,
        group_name: callData.chat.group_name,
        group_icon: callData.chat.group_icon,
        profile_pic: callData.user.profile_pic,
        user_id: callData.user.user_id,
        user_name: callData.user.user_name,
      };

      // To Update current conversation
      dispatch(updateCurrentConversation(conversationData));
      // sessionStorage.setItem(
      //   "currentConversation",
      //   JSON.stringify(conversationData),
      // );

      // To show accept call modal
      dispatch(
        updateViewState({
          show_accept_call_modal: true,
        }),
      );

      // To store call data in session storage (ring only — do NOT mark joined)
      sessionStorage.setItem("call_type", callData.call.call_type);
      sessionStorage.setItem("call_chat_type", callData.chat.chat_type);
      sessionStorage.setItem("is_caller", "false");
      sessionStorage.setItem("room_id", callData.call.room_id);
      sessionStorage.setItem("call_id", callData.call.call_id.toString());
      sessionStorage.setItem("chat_id", callData.chat.chat_id.toString());
      sessionStorage.setItem(
        "media_engine",
        callData.call.media_engine || "livekit",
      );
      sessionStorage.removeItem("livekit_token");
      sessionStorage.removeItem("call_joined");
      sessionStorage.removeItem("callStartTime");
      const incomingIsGroup =
        callData.chat.chat_type === "group" ||
        callData.chat.chat_type === "public_group";
      const incomingTitle = incomingIsGroup
        ? callData.chat.group_name || "Group call"
        : callData.user?.full_name ||
          callData.user?.user_name ||
          "Incoming call";
      const incomingAvatar = incomingIsGroup
        ? callData.chat.group_icon || ""
        : callData.user?.profile_pic || "";
      setCallPeerSnapshot({
        title: incomingTitle,
        avatar: incomingAvatar,
        chatId: callData.chat.chat_id,
        chatType: callData.chat.chat_type,
      });

      // To update peer data — call_joined false until Accept
      dispatch(
        updatePeerData({
          room_id: callData.call.room_id,
          call_id: callData.call.call_id,
          chat_id: callData.chat.chat_id.toString(),
          call_joined: false,
        }),
      );
    });

    // When call declined
    // NOTE: callDecline event will not receive peer_id like UserJoinedRes
    socket.on("call_declined", (callDeclinedData: UserJoinedRes) => {
      beginCallTeardown();
      dispatch(
        updateViewState({
          show_call_declined_modal: true,
          show_accept_call_modal: false,
        }),
      );
      dispatch(updatePeerData(idlePeerCallPatch()));
      leaveCallToChat();
    });

    // When the same user accepted the call on another device (e.g., phone) —
    // dismiss the ringing modal on this device (e.g., web)
    const dismissIncomingElsewhere = () => {
      stopAllCallSounds();
      dispatch(
        updateViewState({
          show_accept_call_modal: false,
        }),
      );
      clearCallSessionKeys();
      dispatch(updatePeerData(idlePeerCallPatch()));
    };
    socket.on("call_accepted_elsewhere", dismissIncomingElsewhere);
    // Declined on another of *our* devices — silent stop ring (not peer-declined UI)
    socket.on("call_declined_elsewhere", dismissIncomingElsewhere);
    socket.on("missed_call", (missedCallData: CallEndedRes) => {
      dispatch(
        updateViewState({
          show_missed_call_modal: true,
          show_accept_call_modal: false,
        }),
      );
      // Keep modal; clear ring session so green bar cannot appear
      stopAllCallSounds();
      clearCallSessionKeys();
      dispatch(updatePeerData(idlePeerCallPatch()));
    });

    socket.on("pinned_unpinned_message", (pinUnpinData: PinUnpinRes) => {
      const record =
        pinUnpinData?.message ||
        pinUnpinData?.Records?.[0] ||
        (pinUnpinData?.message_id != null
          ? ({
              message_id: pinUnpinData.message_id,
              chat_id: pinUnpinData.chat_id,
              pinned: !!pinUnpinData.pinned,
            } as any)
          : null);
      if (!record?.message_id) return;
      const chatId = Number(record.chat_id ?? pinUnpinData.chat_id);
      if (chatId !== Number(currentChatRef.current.chat_id)) return;

      const pinned = !!(record.pinned ?? pinUnpinData.pinned);
      dispatch(
        updateMessagesByIds({
          message_id: record.message_id,
          pinned,
        }),
      );
      if (pinned) {
        dispatch(appendPinnedMessages(record as any));
      } else {
        dispatch(removePinnedMessagesByIds([record.message_id]));
      }

      // Keep disk warm in sync for soft reopen
      const pins = store.getState().MessageList.pinned_messages.Records || [];
      if (!pins.length) clearPinnedCatalogDisk(chatId);
      else writePinnedCatalogDisk(chatId, pins);
    });
    socket.on("block_updates", (blockedUpdateData: BlockedUpdateRes) => {
      dispatch(
        updateByChatId({
          chat_id: blockedUpdateData.chat_id,
          last_message: "",
          last_message_type: "",
          blocked_by: blockedUpdateData.is_blocked
            ? [blockedUpdateData.user_id.toString()]
            : [],
        }),
      );
      if (blockedUpdateData.chat_id == currentChatRef.current.chat_id) {
        // sessionStorage.removeItem("currentConversation");
        if (userDataRef.current?.user_id == blockedUpdateData.user_id) {
          dispatch(
            updateCurrentConversation({
              is_group_blocked: blockedUpdateData.is_blocked,
            }),
          );
        } else {
          dispatch(
            updateCurrentConversation({
              other_user_blocked_you: blockedUpdateData.is_blocked,
            }),
          );
        }
      }
    });

    const resyncChatLists = () => {
      socket.emit("chat_list", chatListFullPayload());
      socket.emit("archived_chat_list", chatListFullPayload());
    };

    const catchUpOpenChat = () => {
      const chat = currentChatRef.current;
      const list = messageListRef.current;
      const opts = store.getState().MessageOptions;
      if (!chat?.chat_id || !list?.message_list?.Records?.length) return;
      // Only tip window — mid-history holes use after/between, not reconnect pts
      if (opts.has_more_newer) return;
      // last locally known id (stale tip in Pagination is useless after missed receives)
      const lastKnown = Math.max(
        0,
        ...list.message_list.Records.filter(isRealMessage).map((m) =>
          Number(m.message_id),
        ),
      );
      const afterId = lastKnown;
      if (!afterId) return;

      void catchUpChatDifference({
        chat_id: Number(chat.chat_id),
        after_message_id: afterId,
        onPage: (page) => {
          if (Number(currentChatRef.current.chat_id) !== Number(page.chat_id)) {
            return;
          }
          if (page.deleted_message_ids?.length) {
            dispatch(removeMessagesByIds(page.deleted_message_ids));
            dispatch(removePinnedMessagesByIds(page.deleted_message_ids));
          }
          if (page.messages?.length) {
            dispatch(appendNewerMessages(page.messages));
          }
          if (page.chat_tip_message_id != null) {
            // keep tip id for next reconnect
            dispatch(
              updateMessageOptions({
                has_more_newer: !!page.has_more_newer,
              }),
            );
          }
        },
      }).catch(() => {
        /* non-fatal */
      });
    };

    const onSocketConnect = () => {
      resyncChatLists();
      catchUpOpenChat();
    };

    // After idle/tab sleep the socket reconnects and used to replace the list
    // with only the first page (default pageSize 10) — always request full list.
    socket.on("connect", onSocketConnect);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && socket.connected) {
        resyncChatLists();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      socket.off("connect", onSocketConnect);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return <></>;
}
